import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import context from "../../store/context";

// layout
import AuthenticationLayout from "./AuthenticationLayout";

import AuthenticationButton from "../../components/Buttons/AuthenticationButton";
import AlertModal from "../../components/UI/AlertModal";

//icons
import ArrowIcon from "../../components/icons/Arrow";

//assets
import TelebirrLogo from "../../assets/telebirr.png";
import LoginPattern from "../../assets/login-pattern.svg";

import {
  getMiniAppToken,
  isInTelebirrSuperApp,
  describeBridgeEnv,
} from "../../utils/telebirrBridge";
import {
  debugLog,
  debugError,
  debugTimer,
  describeError,
  maskToken,
} from "../../utils/debug";

const SCOPE = "login";

const MINIAPP_ENDPOINT = (process.env.REACT_APP_MINIAPP_ENDPOINT || "").trim();
const TELEBIRR_APP_ID = (process.env.REACT_APP_TELEBIRR_APP_ID || "").trim();
// A prefetched SuperApp token older than this is not reused.
const APP_TOKEN_MAX_AGE_MS = 60000;
// The backend gives telebirr 20s per login; past this the request is lost.
const LOGIN_TIMEOUT_MS = 35000;

const LoginPage = () => {
  const [isSending, setIsSending] = useState(false);
  const [alert, setAlert] = useState(null);
  const tokenRequestRef = useRef(null);

  const { setToken } = useContext(context);

  const navigate = useNavigate();

  const showError = (title, message, detail) => {
    debugError(SCOPE, `alert shown: ${title}`, detail ?? message);
    setIsSending(false);
    setAlert({ title, message });
  };

  // Ask the SuperApp for an access token as soon as this screen opens, and
  // reuse that in-flight request when the button is tapped.
  const requestAppToken = () => {
    const current = tokenRequestRef.current;
    if (current && Date.now() - current.startedAt < APP_TOKEN_MAX_AGE_MS) {
      return current.promise;
    }

    const startedAt = Date.now();
    const promise = getMiniAppToken(TELEBIRR_APP_ID).catch((error) => {
      if (tokenRequestRef.current && tokenRequestRef.current.promise === promise) {
        tokenRequestRef.current = null;
      }
      throw error;
    });
    tokenRequestRef.current = { startedAt, promise };
    return promise;
  };

  useEffect(() => {
    if (!TELEBIRR_APP_ID || !MINIAPP_ENDPOINT || !isInTelebirrSuperApp()) return;
    requestAppToken().catch(() => {});
    // One prefetch for this screen. A failed request is cleared and retried on tap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // telebirr-login exchanges the SuperApp access token. The backend reads the
  // phone and name from Telebirr's authToken response.
  const telebirrLogin = async (appToken, attempt = 1) => {
    const url = `${MINIAPP_ENDPOINT}/api/miniapp/telebirr-login`;
    const done = debugTimer(SCOPE, `POST /api/miniapp/telebirr-login (attempt ${attempt})`);
    debugLog(SCOPE, "exchanging SuperApp token", {
      endpoint: MINIAPP_ENDPOINT,
      token: maskToken(appToken),
    });

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: appToken }),
        signal: controller.signal,
      });

      const raw = await res.text().catch(() => "");
      let json = null;
      try {
        json = raw ? JSON.parse(raw) : null;
      } catch (_) {
        // Gateway errors and login redirects come back as HTML, not JSON —
        // seeing the first slice of the body is what makes that obvious.
        debugError(SCOPE, "response was not JSON", raw.slice(0, 300));
      }

      done(`HTTP ${res.status}`, {
        ok: res.ok,
        redirected: res.redirected,
        finalUrl: res.url,
        contentType: res.headers.get("content-type"),
        body: json
          ? { ...json, token: json.token ? maskToken(json.token) : json.token }
          : raw.slice(0, 300),
      });

      if (json && json.status && json.token) {
        // Backend logs in existing users and auto-registers new ones
        // (same as Kacha / CBE SuperApp), then returns a JWT.
        debugLog(SCOPE, "login ok → /tenders", { jwt: maskToken(json.token) });
        setIsSending(false);
        sessionStorage.setItem("token", json.token);
        setToken(json.token);
        navigate("/tenders");
      } else {
        showError(
          "Couldn't Sign You In",
          "Something went wrong. Please try again.",
          { httpStatus: res.status, status: json && json.status, message: json && json.message }
        );
      }

      return json;
    } catch (error) {
      if (error && error.name === "AbortError") {
        done("timeout", { afterMs: LOGIN_TIMEOUT_MS });
        showError(
          "Taking Too Long",
          "iChereta is taking too long to respond. Please try again.",
          { step: "telebirr-login", timeoutMs: LOGIN_TIMEOUT_MS }
        );
        return null;
      }

      done("network error", describeError(error));

      // Dropped connections are often one-offs on mobile networks. Retry once
      // with a fresh SuperApp token, since the first may have reached the
      // backend and been spent.
      if (attempt === 1) {
        debugLog(SCOPE, "retrying login once with a fresh SuperApp token");
        try {
          const freshToken = await requestAppToken();
          tokenRequestRef.current = null;
          return telebirrLogin(freshToken, 2);
        } catch (tokenError) {
          debugError(SCOPE, "fresh token for retry failed", describeError(tokenError));
        }
      }

      showError(
        "Connection Problem",
        "We couldn't reach iChereta. Check your connection and try again.",
        describeError(error)
      );
      return null;
    } finally {
      clearTimeout(timer);
    }
  };

  const logUserIn = () => {
    debugLog(SCOPE, "login tapped", {
      endpoint: MINIAPP_ENDPOINT || "<missing>",
      appId: TELEBIRR_APP_ID || "<missing>",
      bridge: describeBridgeEnv(),
    });

    if (!isInTelebirrSuperApp()) {
      debugError(SCOPE, "not running inside the telebirr SuperApp", describeBridgeEnv());
      setAlert({
        title: "Open in telebirr SuperApp",
        message:
          "This app needs to be opened from within the telebirr SuperApp. Please launch iChereta from the telebirr SuperApp to continue.",
      });
      return;
    }

    if (!TELEBIRR_APP_ID) {
      showError(
        "Couldn't Sign You In",
        "telebirr app configuration is missing. Please contact support.",
        "REACT_APP_TELEBIRR_APP_ID is empty at build time"
      );
      return;
    }

    if (!MINIAPP_ENDPOINT) {
      showError(
        "Couldn't Sign You In",
        "telebirr app configuration is missing. Please contact support.",
        "REACT_APP_MINIAPP_ENDPOINT is empty at build time"
      );
      return;
    }

    setIsSending(true);
    const pending = requestAppToken();
    // The token is spent by this login. A retry must ask Telebirr for a new one.
    tokenRequestRef.current = null;
    pending
      .then((token) => telebirrLogin(token))
      .catch((err) => {
        showError(
          "Couldn't Sign You In",
          "Something went wrong. Please try again.",
          { step: "getMiniAppToken", error: describeError(err) }
        );
      });
  };

  return (
    <AuthenticationLayout
      button="Sign Up"
      route="/signup"
      message="Don't have an account?"
      footerClassName="text-[#d8ccf7]"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-0 h-[85%] bg-gradient-to-t from-[#664bc7]/80 via-[#664bc7]/30 via-60% to-transparent"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-0 h-[85%]"
        style={{
          backgroundImage: `url(${LoginPattern})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "bottom",
          opacity: 0.6,
        }}
      />
      <div className="relative z-10 w-full flex flex-col items-center space-y-5">
        <h1 className="font-bold text-lg text-[#5a4aa6]">ሁሉንም ጨረታ በአንድ ቦታ!</h1>
        <small className="text-center text-xs font-bold text-[#5a4aa6]">በመረጧቸው ዘርፎች ያሉ የጨረታ ዝርዝር መረጃዎችን በቀላሉ ይግኙ!</small>
        <div className=" animate-bounce">
          <ArrowIcon styles="w-8 h-8 rotate-90 text-[#5a4aa6]" />
        </div>
        <div className="w-[90%] max-w-[600px] bg-transparent flex flex-col flex-grow  sm:p-10 md:rounded-md space-y-3 md:space-y-5 ">
          <AuthenticationButton
            process={isSending}
            className="w-full h-12 md:h-[52px] bg-white text-[#3a2f6b] text-base md:text-lg rounded-xl overflow-hidden relative border border-gray-200 shadow-[0_2px_4px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.9)] hover:shadow-[0_4px_10px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,0.9)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.12)] active:translate-y-px transition-all duration-200"
            name={
              <span className="flex items-center justify-center gap-3 font-bold">
                <img
                  src={TelebirrLogo}
                  alt="telebirr SuperApp"
                  decoding="sync"
                  loading="eager"
                  className="h-6 object-contain"
                />
                <span className="h-6 w-px bg-gray-300" />
                Login with telebirr
              </span>
            }
            onClick={logUserIn}
          />
        </div>
      </div>

      {alert && (
        <AlertModal
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </AuthenticationLayout>
  );
};

export default LoginPage;
